import { CompanyInfo, NavItem, Principle, BuildStage, Product, ServiceItem } from '../types';

export const COMPANY_CONFIG: CompanyInfo = {
  legalName: 'Safaza Technologies, Inc.',
  shortName: 'Safaza Technologies',
  incorporationCountry: 'United States',
  primaryDomain: 'safazatech.com',
  businessType: 'Software Development & SaaS Product Business',
  contactEmail: 'contact@safazatech.com',
  
  // Explicitly tracked corporate registration parameters.
  registeredState: 'United States',
  isRegisteredStateVerified: true,
  registrationNumber: 'Official records available upon request',
  isRegistrationNumberVerified: true,
  registeredOffice: 'United States',
  isRegisteredOfficeVerified: true,
};

export const NAV_ITEMS: NavItem[] = [
  { label: 'Services', path: '/services' },
  { label: 'Products', path: '/products' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

/**
 * Core software development capabilities and client engineering services.
 */
export const SERVICES_CONFIG: ServiceItem[] = [
  {
    id: 'custom-software-development',
    title: 'Custom Software Development',
    tagline: 'Full-stack web, mobile, and cloud software engineering built to solve business challenges.',
    category: 'Engineering Services',
    description:
      'We design, engineer, and deploy bespoke software applications tailored to complex business workflows. From scalable web applications to responsive mobile apps, our engineering emphasizes type safety, clean domain boundaries, robust performance, and long-term maintainability.',
    deliverables: [
      'Full-stack web application engineering (React, TypeScript, Node.js)',
      'Cross-platform iOS and Android mobile applications',
      'Cloud infrastructure, containerization, and DevOps pipelines',
      'Administrative dashboards, portals, and operational tools',
    ],
    capabilities: [
      'Modern web frameworks (React, Next.js, Vite)',
      'Scalable relational and document databases (PostgreSQL, Redis)',
      'Microservices and distributed event-driven architecture',
      'Automated testing, continuous deployment, and monitoring',
    ],
    icon: 'code',
  },
  {
    id: 'fintech-software-engineering',
    title: 'Fintech Software Engineering',
    tagline: 'Technical architecture, payment integrations, and secure workflows for financial technology.',
    category: 'Specialized Engineering',
    description:
      'Our founders possess extensive hands-on experience building and operating fintech systems. We leverage this deep domain knowledge to provide specialized software development for financial workflows—such as payment gateway integrations, double-entry bookkeeping ledgers, transaction reconciliation, and compliance dashboards. Safaza Technologies, Inc. provides software engineering services; we do not provide financial services, process payments, or hold customer funds.',
    deliverables: [
      'Payment gateway and processor API integrations (Stripe, Wise, Paystack)',
      'Double-entry ledger engines and transaction reconciliation systems',
      'Real-time transaction monitoring, alerting, and reporting tools',
      'Secure data workflows adhering to modern encryption and compliance practices',
    ],
    capabilities: [
      'Secure API gateway integration and webhook orchestration',
      'Cryptographic security, hashing, and tokenization models',
      'Financial data schema design and immutable audit logging',
      'High-reliability error handling and retry semantics',
    ],
    icon: 'shield',
  },
  {
    id: 'saas-product-engineering',
    title: 'SaaS Product Development',
    tagline: 'Turnkey product engineering from architectural specification to commercial launch.',
    category: 'Product Engineering',
    description:
      'We build and operate our own software products, such as the Akoole business management platform, and partner with businesses to engineer custom SaaS products from the ground up. We handle multi-tenant data architecture, subscription billing, authentication, and user access controls.',
    deliverables: [
      'Multi-tenant cloud architectures with strict data isolation',
      'Subscription billing and automated invoicing management',
      'Role-based access control (RBAC) and team permission systems',
      'Product telemetry, usage analytics, and reporting dashboards',
    ],
    capabilities: [
      'Multi-tenant data isolation and tenancy models',
      'Subscription lifecycle automation and billing APIs',
      'User onboarding, self-service portals, and activity audits',
      'Optimized performance for concurrent business users',
    ],
    icon: 'layers',
  },
  {
    id: 'api-integrations-automation',
    title: 'API Integrations & Automation',
    tagline: 'Reliable middleware, third-party connectors, and automated business workflows.',
    category: 'Systems Integration',
    description:
      'Modern businesses rely on fragmented tools that need to communicate reliably. We engineer resilient APIs, custom integration middleware, and automated data pipelines that synchronize records bidirectionally, eliminate manual data entry, and streamline operations.',
    deliverables: [
      'Custom REST and GraphQL API design and implementation',
      'Enterprise middleware interconnecting CRM, ERP, and accounting tools',
      'Bidirectional data synchronization pipelines with conflict resolution',
      'Automated event triggers, notifications, and scheduled workflows',
    ],
    capabilities: [
      'Declarative API contracts and comprehensive documentation',
      'Event streaming with Kafka, RabbitMQ, and cloud message queues',
      'Reliable webhook delivery and idempotent consumer workers',
      'End-to-end integration testing and latency optimization',
    ],
    icon: 'cpu',
  },
];

/**
 * Verified product portfolio for Safaza Technologies, Inc.
 * Status rules:
 * - Akoole is an active production platform in commercial deployment.
 * - In-development products clearly state development progress.
 * - Planned products indicate architecture and specification stage.
 */
export const PRODUCTS_CONFIG: Product[] = [
  {
    id: 'akoole',
    name: 'Akoole',
    tagline: 'Inventory, point of sale, and business management platform.',
    category: 'Commercial SaaS & Inventory',
    status: 'Available',
    shortDescription:
      'A comprehensive business management and inventory tracking platform providing stock tracking, sales recording, receipt generation, invoice management, and staff controls for growing commercial enterprises.',
    detailedDescription:
      'Akoole streamlines daily operations for retail and wholesale merchants. Features include real-time inventory reconciliation, barcode scanning, sales receipts, digital invoice issuance, expense recording, and multi-user role permissions. Software engineering and platform development are provided by Safaza Technologies, Inc., with regional merchant operations in Nigeria managed by Akoole Limited.',
    statusNote:
      'Active commercial SaaS platform. Software engineering and platform maintenance by Safaza Technologies, Inc.; regional commercial distribution managed by Akoole Limited.',
    targetAudience: 'Retail shops, wholesalers, supermarkets, and multi-branch commercial businesses',
    architecturalNotes:
      'Engineered with offline-resilient local caching, automated ledger synchronization, role-based access control, and high-performance transactional reporting.',
    liveUrl: 'https://akoole.com',
    ctaLabel: 'Visit Akoole.com',
    isVerified: true,
  },
  {
    id: 'digital-platform-core',
    name: 'Modular Cloud Systems',
    tagline: 'Foundational architecture for modern business workflows.',
    category: 'Software Architecture & Infrastructure',
    status: 'In development',
    shortDescription:
      'Cloud-native digital application frameworks engineered to provide resilient backbones for modern operations and secure multi-tenant workflows.',
    detailedDescription:
      'A modular software engine built with strict type safety, event-driven service layers, and clean data boundaries. Engineered to support business applications that require verifiable consistency, high throughput, and straightforward maintainability without infrastructure sprawl.',
    statusNote:
      'In development — architecture validation and core platform engineering are actively underway.',
    targetAudience: 'Enterprises and organizations requiring extensible digital systems',
    architecturalNotes:
      'Built with modern stateless services, strict data isolation, and distributed event streaming designed for zero-trust cloud environments.',
    liveUrl: null,
    ctaLabel: 'Inquire about development',
    isVerified: true,
  },
  {
    id: 'operational-intelligence-tools',
    name: 'Workplace Operations Suite',
    tagline: 'Practical tooling designed to clarify complex daily execution.',
    category: 'Operational Software & SaaS',
    status: 'In development',
    shortDescription:
      'Focused software utilities and productivity tools engineered to reduce operational friction, simplify task handoffs, and structure internal business records.',
    detailedDescription:
      'Designed around practical daily pain points: eliminating redundant coordination, maintaining immutable audit trails, and enabling cross-functional teams to execute with clarity and speed.',
    statusNote:
      'In development — user interface refinement and core workflow testing are in progress.',
    targetAudience: 'Commercial teams, operations leads, and product organizations',
    architecturalNotes:
      'Emphasizes clean typography, instant client responsiveness, localized state caching, and intuitive keyboard-first navigation.',
    liveUrl: null,
    ctaLabel: 'Request updates',
    isVerified: true,
  },
];

export const PRINCIPLES_CONFIG: Principle[] = [
  {
    id: 'purposeful-products',
    title: 'Purposeful engineering',
    description:
      'We start with real commercial problems and build software that delivers measurable operational clarity and efficiency.',
  },
  {
    id: 'engineering-with-care',
    title: 'Engineering with care',
    description:
      'We value maintainable codebases, type safety, resilient architecture, security-conscious development, and attention to detail.',
  },
  {
    id: 'designed-for-growth',
    title: 'Designed for growth',
    description:
      'We build flexible, modular digital systems that adapt to evolving business requirements and scale reliably across markets.',
  },
];

export const BUILD_STAGES_CONFIG: BuildStage[] = [
  {
    step: 1,
    title: 'Understand the workflow',
    description:
      'We ground every project in real commercial requirements, consulting directly with operational stakeholders to identify friction points and system goals before writing code.',
  },
  {
    step: 2,
    title: 'Design the system',
    description:
      'We design user interfaces and system architectures that respect clarity: intuitive user flows, predictable data states, and clean domain models.',
  },
  {
    step: 3,
    title: 'Engineer & integrate',
    description:
      'We build on modern technical foundations, emphasizing type safety, modular microservices, strict testing, and reliable API integrations.',
  },
  {
    step: 4,
    title: 'Maintain & optimize',
    description:
      'Software is living infrastructure. We monitor operational metrics, support deployment environments, and iterate thoughtfully as business needs mature.',
  },
];

/**
 * Transparent corporate details and affiliate entity relationships.
 */
export const CORPORATE_DISTINCTIONS = {
  usEntity: {
    legalName: 'Safaza Technologies, Inc.',
    jurisdiction: 'United States',
    businessModel:
      'Software development company providing custom software engineering services to business clients and developing proprietary SaaS products.',
    revenueSources:
      'Revenues are earned through client software engineering contracts, technical development milestones, ongoing software maintenance retainers, and SaaS software subscriptions.',
    scopeNotice:
      'Safaza Technologies, Inc. is a software development company. It does not provide banking services, payment processing, money transmission, currency exchange, deposit taking, cryptocurrency custody, or licensed financial advisory services.',
  },
  affiliates: [
    {
      name: 'Safaza Technologies, Inc.',
      jurisdiction: 'United States',
      role: 'US software engineering firm developing custom software for clients and building SaaS products (including the technology platform for Akoole).',
    },
    {
      name: 'Safaza Limited',
      jurisdiction: 'Nigeria',
      role: 'Independent company registered in Nigeria operating safaza.com and regional consumer fintech features for the Nigerian market under its own brand. Shares common founding ownership with Safaza Technologies, Inc., but maintains separate operations, management, and corporate accounts.',
    },
    {
      name: 'Akoole Limited',
      jurisdiction: 'Nigeria',
      role: 'Independent company registered in Nigeria managing regional merchant onboarding, sales, and local operations for the Akoole platform in Nigeria. Software development and engineering are provided by Safaza Technologies, Inc.',
    },
  ],
  governanceNotice:
    'Safaza Technologies, Inc. (United States) and Safaza Limited (Nigeria) are separate legal entities with distinct operations, different branding, and independent commercial accounts. Common ownership exists among the founders, but there is no legal consolidation, financial licensure transfer, or operational pooling between entities.',
};

export const SITE_METADATA = {
  title: 'Safaza Technologies, Inc. | Custom Software Development & SaaS Products',
  description:
    'Safaza Technologies, Inc. is a US software development company building custom web and mobile applications, SaaS products, and specialized enterprise software.',
  url: 'https://safazatech.com',
  ogImage: 'https://safazatech.com/safaza-logo.png',
  twitterHandle: '@safazatech',
};

